import { TEMPLATE_TOPIC_FILTERS } from '@/utils/templateTags';
import Constants from 'expo-constants';

const KNOWN_TEMPLATE_TOPICS = new Set(
  TEMPLATE_TOPIC_FILTERS.map((entry) => entry.tag.toLowerCase()),
);

/** Marketing / blog query values → canonical template topic tags. */
const TOPIC_ALIASES: Record<string, string> = {
  'america250': 'america-250',
  'america-250': 'america-250',
  'world-cup-2026': 'world-cup-2026',
  'worldcup2026': 'world-cup-2026',
  'world-cup': 'world-cup-2026',
  'calgary-stampede': 'calgary-stampede',
  'calgarystampede': 'calgary-stampede',
  'stampede': 'calgary-stampede',
};

export const TEMPLATES_DEEP_LINK_PATH = '/templates' as const;
const DEFAULT_WEB_APP_ORIGIN = 'https://app.tripvio.app';

function getWebAppOrigin(): string {
  const fromEnv = process.env.EXPO_PUBLIC_WEB_APP_URL;
  const fromExtra =
    (Constants.expoConfig?.extra?.EXPO_PUBLIC_WEB_APP_URL as string | undefined) ||
    (Constants.expoConfig?.extra?.webAppUrl as string | undefined);
  const raw = (fromEnv || fromExtra || DEFAULT_WEB_APP_ORIGIN).trim();
  try {
    return new URL(raw).origin;
  } catch {
    return DEFAULT_WEB_APP_ORIGIN;
  }
}

export function normalizeTemplateTopicParam(
  raw: string | string[] | undefined,
): string | undefined {
  const value = Array.isArray(raw) ? raw[0] : raw;
  const trimmed = String(value ?? '').trim().toLowerCase();
  if (!trimmed) {
    return undefined;
  }
  const aliased = TOPIC_ALIASES[trimmed] ?? trimmed.replace(/\s+/g, '-');
  if (!KNOWN_TEMPLATE_TOPICS.has(aliased)) {
    return undefined;
  }
  return aliased;
}

export function normalizeTemplateIdParam(
  raw: string | string[] | undefined,
): string | undefined {
  const value = Array.isArray(raw) ? raw[0] : raw;
  const trimmed = String(value ?? '').trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function buildTemplatesTabParams(opts: {
  topic?: string;
  templateId?: string;
  ref?: string;
  utmSource?: string;
}): Record<string, string> {
  const params: Record<string, string> = { openTemplates: '1' };
  if (opts.topic) {
    params.templateTopic = opts.topic;
  }
  if (opts.templateId) {
    params.templateId = opts.templateId;
  }
  if (opts.ref) {
    params.ref = opts.ref;
  }
  if (opts.utmSource) {
    params.utm_source = opts.utmSource;
  }
  return params;
}

export function buildTemplateShareUrl(opts: {
  templateId?: string;
  topic?: string;
  source?: string;
}): string {
  const url = new URL(TEMPLATES_DEEP_LINK_PATH, getWebAppOrigin());
  const topic = normalizeTemplateTopicParam(opts.topic);
  const templateId = normalizeTemplateIdParam(opts.templateId);
  if (topic) {
    url.searchParams.set('topic', topic);
  }
  if (templateId) {
    url.searchParams.set('templateId', templateId);
  }
  url.searchParams.set('ref', 'template_share');
  url.searchParams.set('utm_medium', 'app_share');
  url.searchParams.set('utm_source', opts.source || 'tripvio_app');
  return url.toString();
}

export function isTemplatesDeepLinkPath(parts: string[]): boolean {
  return parts[0] === 'templates';
}
